import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IIntegration from '../../interfaces/IIntegration'
import IIntegrationSettings from '../../interfaces/IIntegrationSettings'
import IIntegrationSync from '../../interfaces/IIntegrationSync'
import { SearchRequest } from '../../services/api/interfaces/ApiRequest'
import { integrationList } from '../../services/api'
import { loadStorage } from '../../services/storage'
import { STORAGE_KEY } from '../AuthContext'

// Integration API
import {
  PartnerAuthRequest,
  UpdateSettingsRequest,
  UpdateSyncSectionRequest,
  SyncOneRequest,
  SyncAbortRequest,
  VerifySettingsRequest,
  VerifySyncRequest,
  VerifySyncSectionRequest,
  VerifyShelterRequest,
  RetrieveTotalPetRequest,
  ConnectRequest,
  DisconnectRequest,
  SetMainRequest,
  PreviewRequest,
  NotifyAdoptionInfoRequest,
  VerifyNetworkSettingsRequest
} from '../../services/integration/interfaces/ApiRequest'
import {
  setToken,
  partnerAuth,
  syncOne,
  syncAbort,
  updateSettings,
  updateSyncSection,
  verifySettings,
  verifySync,
  verifySyncSection,
  verifyShelter,
  retrieveTotalPet,
  connect,
  disconnect,
  setMain,
  preview,
  notifyAdoptionInfo,
  integrationNetworkList,
  integrationNetworkCreate,
  integrationDefinitionsCreate,
  integrationDefinitionsList,
  integrationNetworkUpdate,
  integrationDefinitionsUpdate,
  verifyNetworkSettings,
  integrationNetworkListSimple
} from '../../services/integration'

import IIntegrationSyncSection from '../../interfaces/IIntegrationSyncSection'
import IIntegrationShelter from '../../interfaces/IIntegrationShelter'
import IIntegrationPreview from '../../interfaces/IIntegrationPreview'

interface IntegrationContextState extends BaseContextState {
  integration: IIntegration | null,
  integrations: Array<IIntegration> | null,
  settings: IIntegrationSettings | null,
  sync: IIntegrationSync | null,
  syncSections: IIntegrationSyncSection[] | null,
  authUuid: string | null,
  shelterIntegrations: IIntegrationShelter[] | null
  total: number
  preview: IIntegrationPreview | null
}

export interface IntegrationContextData extends IntegrationContextState {
  resetErrors: Function,
  requestIntegrationList: (args: SearchRequest) => void,
  requestIntegrationFind: (args: SearchRequest) => void,
  requestNetworkFind: (args: SearchRequest) => void,
  requestIntegrationNetworkList: (args: SearchRequest) => void,
  requestIntegrationNetworkListSimple: (args: any) => void,
  requestIntegrationNetworkCreate: (args: SearchRequest) => Promise<any>,
  requestIntegrationNetworkUpdate: (args: SearchRequest) => Promise<any>,
  requestIntegrationDefinitionsCreate: (args: SearchRequest) => Promise<any>,
  requestIntegrationDefinitionsUpdate: (args: SearchRequest) => Promise<any>,
  requestIntegrationDefinitionsList: (args: SearchRequest) => void,
  requestPartnerAuth: (args: PartnerAuthRequest) => Promise<any>,

  requestVerifySettings: (args: VerifySettingsRequest) => Promise<any>,
  requestVerifyNetworkSettings: (args: VerifyNetworkSettingsRequest) => Promise<any>,
  requestVerifySync: (args: VerifySyncRequest) => Promise<any>,
  requestVerifySyncSections: (args: VerifySyncSectionRequest) => Promise<any>,
  requestVerifyShelter: (args: VerifyShelterRequest) => Promise<any>,
  requestRetrieveTotalPet: (args: RetrieveTotalPetRequest) => Promise<any>,

  requestUpdateSettings: (args: UpdateSettingsRequest) => Promise<any>,
  requestUpdateSyncSections: (args: UpdateSyncSectionRequest) => Promise<any>,
  requestSyncOne: (args: SyncOneRequest) => Promise<any>,
  requestSyncAbort: (args: SyncAbortRequest) => Promise<any>,

  requestConnect: (args: ConnectRequest) => Promise<any>,
  requestDisconnect: (args: DisconnectRequest) => Promise<any>,
  requestSetMain: (args: SetMainRequest) => Promise<any>,
  requestPreview: (args: PreviewRequest) => Promise<any>,

  requestNotifyAdoptionInfo: (args: NotifyAdoptionInfoRequest) => Promise<any>,
}

// Create context
const { Provider, Consumer } = createContext({} as IntegrationContextData)

// Export consumer
export const IntegrationConsumer = Consumer

export class IntegrationProvider extends BaseContextProvider {
  state: IntegrationContextState = {
    integration: null,
    integrations: null,
    settings: null,
    sync: null,
    syncSections: null,
    authUuid: null,
    shelterIntegrations: null,
    total: 0,
    preview: null
  }

  lastListPayload: SearchRequest | null = null

  /**
   * Setup admin account session to integration API
   */
  componentDidMount() {
    const store = loadStorage(STORAGE_KEY)

    if (store) {
      setToken(store.accessToken)
    }
  }

  /**
   * Make api request for integration list
   * 
   * @param {SearchRequest} payload 
   */
   requestIntegrationList = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      authUuid: null,
      integration: null,
      settings: null
    }, async () => {
      this.lastListPayload = payload
      const response = await integrationList(payload)

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'integrations'
        },
      })
    })
  }

  /**
   * Make api request for integration finder
   * 
   * @param {SearchRequest} payload 
   */
   requestIntegrationFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      integration: null,
      settings: null,
      sync: null,
      syncSections: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }
      const response = await integrationList(payload)

      // Process our response
      this.processResponse(response, {
        mappings: {
          destination: 'integration'
        }
      })
    })
  }

  /* Make api request for integration finder
  * 
  * @param {SearchRequest} payload 
  */
  requestNetworkFind = (payload: SearchRequest) => {
   this.setState({
     fetching: true,
     error: null,
     integration: null,
     settings: null,
     sync: null,
     syncSections: null
   }, async () => {
     payload.options = {
       ...payload.options,
       _single: true
     }
     const response = await integrationNetworkList(payload)

     // Process our response
     this.processResponse(response, {
       mappings: {
         destination: 'integration'
       }
     })
   })
 }

  /**
   * Make api request for integration list
   * 
   * @param {SearchRequest} payload 
   */
   requestIntegrationNetworkList = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      authUuid: null,
      integration: null,
      settings: null
    }, async () => {
      this.lastListPayload = payload
      const response = await integrationNetworkList(payload)

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'integrations'
        },
      })
    })
  }

  /**
   * Make api request for integration list
   * 
   * @param {SearchRequest} payload 
   */
   requestIntegrationNetworkListSimple = (payload: any) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        authUuid: null,
        integration: null,
        settings: null
      }, async () => {
        this.lastListPayload = payload
        const response = await integrationNetworkListSimple(payload)

        // Process our response
        this.processResponse(response, {})
        resolve(response);
      })
    })
  }

  /**
   * Make api request for integration list
   * 
   * @param {SearchRequest} payload 
   */
   requestIntegrationNetworkCreate = (payload: SearchRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        authUuid: null,
        integration: null,
        settings: null
      }, async () => {
        let response = await integrationNetworkCreate(payload)
        this.processResponse(response, {
        })
        resolve(response);
        //reject(this.state.error)
      })
    })
  }

  /**
   * Make api request for integration list
   * 
   * @param {SearchRequest} payload 
   */
   requestIntegrationNetworkUpdate = (payload: SearchRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        authUuid: null,
        integration: null,
        settings: null
      }, async () => {
        let response = await integrationNetworkUpdate(payload)
        this.processResponse(response, {

        })
        resolve(response);
        //reject(this.state.error)
      })
    })
  }

  /**
   * Make api request for integration list
   * 
   * @param {SearchRequest} payload 
   */
   requestIntegrationDefinitionsList = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      authUuid: null,
      integration: null,
      settings: null
    }, async () => {
      this.lastListPayload = payload
      const response = await integrationDefinitionsList(payload)

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'integrations'
        },
      })
    })
  }

  /**
   * Make api request for integration list
   * 
   * @param {SearchRequest} payload 
   */
   requestIntegrationDefinitionsCreate = (payload: SearchRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        authUuid: null,
        integration: null,
        settings: null
      }, async () => {
        let response = await integrationDefinitionsCreate(payload)
        this.processResponse(response, {
        })
        resolve(response);
        //reject(this.state.error)
      })
    })
  }

  /**
   * Make api request for integration list
   * 
   * @param {SearchRequest} payload 
   */
   requestIntegrationDefinitionsUpdate = (payload: SearchRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        authUuid: null,
        integration: null,
        settings: null
      }, async () => {
        let response = await integrationDefinitionsUpdate(payload)
        this.processResponse(response, {
        })
        resolve(response);
        //reject(this.state.error)
      })
    })
  }


  /**
   * Make api request for integration auth
   * 
   * @param {PartnerAuthRequest} payload 
   */
   requestPartnerAuth = (payload: PartnerAuthRequest) => {
    return new Promise(async (resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        authUuid: null,
        integration: null,
        settings: null,
        sync: null,
        syncSections: null
      }, async () => {
        let response = await partnerAuth(payload)
        this.processResponse(response, {
          keepFetching: true,
          mappings: [
            { origin: 'uuid', destination: 'authUuid' },
            { origin: 'total', destination: 'total' }
          ]
        })
  
        if (this.state.success) {
          await this.requestIntegrationFind({
            search: {
              uuid: this.state.authUuid,
              deleted: false,
              shelter: {
                with: {
                  contract_options: {
                    with: true
                  }
                }
              },
              branch: {
                with: true
              },
              /*integration_syncs: {
                with: {
                  _orderBy: [
                    {
                      column: "id",
                      sort: "DESC"
                    }
                  ],
                  _limit: 1,
                  deleted: false
                }
              }*/
            }
          })

          resolve(this.state.authUuid)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration sync fetch
   * 
   * @param {VerifySyncRequest} payload 
   */
   requestVerifySync = async (payload: VerifySyncRequest) => {
    return await new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {

        let response = await verifySync(payload)
        this.processResponse(response, {
          mappings: { destination: 'sync' }
        })

  
        if (this.state.success) {
          resolve(this.state.sync)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration sync sections fetch
   * 
   * @param {VerifySyncSectionRequest} payload 
   */
   requestVerifySyncSections = (payload: VerifySyncSectionRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await verifySyncSection(payload)
        this.processResponse(response, {
          mappings: { destination: 'syncSections' }
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration settings fetch
   * 
   * @param {VerifySettingsRequest} payload 
   */
   requestVerifySettings = (payload: VerifySettingsRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await verifySettings(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'settings', destination: 'settings' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }


  /**
   * Make api request for integration settings fetch
   * 
   * @param {VerifySettingsRequest} payload 
   */
   requestVerifyNetworkSettings = (payload: VerifyNetworkSettingsRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await verifyNetworkSettings(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'settings', destination: 'settings' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

   /**
   * Make api request for integration shelter fetch
   * 
   * @param {VerifyShelterRequest} payload 
   */
    requestVerifyShelter = (payload: VerifyShelterRequest) => {
      return new Promise((resolve, reject) => {
        this.setState({
          fetching: true,
          error: null,
          shelter: null
        }, async () => {
          let response:any = await verifyShelter(payload)
          this.processResponse(response, {
            mappings: { destination: 'shelterIntegrations' }
          })
    
          if (this.state.success) {
            resolve(this.state.shelterIntegrations)
          } else {
            reject(this.state.error)
          }
        })
      })
    }

  /**
   * Make api request for integration retrieve total pet fetch
   * 
   * @param {RetrieveTotalPetRequest} payload 
   */
   requestRetrieveTotalPet = (payload: RetrieveTotalPetRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await retrieveTotalPet(payload)
        this.processResponse(response, {
          mappings: { destination: 'total' }
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration update settings
   * 
   * @param {UpdateSettingsRequest} payload 
   */
   requestUpdateSettings = (payload: UpdateSettingsRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await updateSettings(payload)
        this.processResponse(response, {
          mappings: [
            { origin: 'settings', destination: 'settings' },
            { origin: 'total', destination: 'total' }
          ]
        })
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration update sync sections
   * 
   * @param {UpdateSyncSectionRequest} payload 
   */
   requestUpdateSyncSections = (payload: UpdateSyncSectionRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await updateSyncSection(payload)
        this.processResponse(response, {
          mappings: { destination: 'syncSections' }
        })
  
        if (this.state.success) {
          resolve(this.state.syncSections)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration sync one
   * 
   * @param {SyncOneRequest} payload 
   */
   requestSyncOne = (payload: SyncOneRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await syncOne(payload)
        this.processResponse(response, {
          mappings: { origin: 'sync', destination: 'sync' }
        })
  
        if (this.state.success) {
          resolve(this.state.sync)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration sync abort
   * 
   * @param {SyncAbortRequest} payload 
   */
   requestSyncAbort = (payload: SyncAbortRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await syncAbort(payload)
        this.processResponse(response, {
          mappings: { origin: 'sync', destination: 'sync' }
        })
  
        if (this.state.success) {
          resolve(this.state.sync)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration connect
   * 
   * @param {ConnectRequest} payload 
   */
   requestConnect = (payload: ConnectRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await connect(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration connect
   * 
   * @param {DisconnectRequest} payload 
   */
   requestDisconnect = (payload: DisconnectRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await disconnect(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration set main
   * 
   * @param {SetMainRequest} payload 
   */
   requestSetMain = (payload: SetMainRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await setMain(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration preview
   * 
   * @param {PreviewRequest} payload 
   */
  requestPreview = (payload: PreviewRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await preview(payload)
        this.processResponse(response, {
          mappings: {
            destination: 'preview'
          }
        })

        if (this.state.success) {
          resolve(this.state.preview)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for integration notify adoption info
   * 
   * @param {NotifyAdoptionInfoRequest} payload 
   */
   requestNotifyAdoptionInfo = (payload: NotifyAdoptionInfoRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        let response = await notifyAdoptionInfo(payload)
        this.processResponse(response)
  
        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestIntegrationList: this.requestIntegrationList,
          requestIntegrationFind: this.requestIntegrationFind,
          requestNetworkFind: this.requestNetworkFind,
          requestIntegrationNetworkList: this.requestIntegrationNetworkList,
          requestIntegrationNetworkListSimple: this.requestIntegrationNetworkListSimple,
          requestIntegrationNetworkCreate: this.requestIntegrationNetworkCreate,
          requestIntegrationNetworkUpdate: this.requestIntegrationNetworkUpdate,
          requestIntegrationDefinitionsCreate: this.requestIntegrationDefinitionsCreate,
          requestIntegrationDefinitionsUpdate: this.requestIntegrationDefinitionsUpdate,
          requestIntegrationDefinitionsList: this.requestIntegrationDefinitionsList,
          requestPartnerAuth: this.requestPartnerAuth,
          requestVerifySettings: this.requestVerifySettings,
          requestVerifyNetworkSettings: this.requestVerifyNetworkSettings,
          requestVerifySync: this.requestVerifySync,
          requestVerifySyncSections: this.requestVerifySyncSections,
          requestVerifyShelter: this.requestVerifyShelter,
          requestRetrieveTotalPet: this.requestRetrieveTotalPet,

          requestUpdateSettings: this.requestUpdateSettings,
          requestUpdateSyncSections: this.requestUpdateSyncSections,
          requestSyncOne: this.requestSyncOne,
          requestSyncAbort: this.requestSyncAbort,
          requestConnect: this.requestConnect,
          requestDisconnect: this.requestDisconnect,
          requestSetMain: this.requestSetMain,
          requestPreview: this.requestPreview,
          requestNotifyAdoptionInfo: this.requestNotifyAdoptionInfo
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
